// src/pages/services/TravelInsurance.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const TravelInsurance = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "Having travel insurance from Excellent Travels saved our vacation when my husband needed emergency medical care in Thailand. The support team was responsive and handled everything efficiently.",
            name: "Patricia Wilson",
            location: "Melbourne, Australia",
            avatar: "/images/testimonials/person11.jpg"
        },
        {
            content: "When our flights were canceled due to bad weather, our travel insurance covered the extra accommodation and new flights. The claims process was straightforward and quick.",
            name: "Daniel Martinez",
            location: "Madrid, Spain",
            avatar: "/images/testimonials/person12.jpg"
        }
    ];

    const faqs = [
        {
            question: "Why do I need travel insurance?",
            answer: "Travel insurance protects you from financial losses due to unexpected events such as trip cancellations, medical emergencies, lost luggage, flight delays, and more. It provides peace of mind and can save you from significant expenses when things don't go as planned."
        },
        {
            question: "What does travel insurance typically cover?",
            answer: "Most travel insurance policies cover emergency medical expenses, trip cancellation/interruption, lost or delayed baggage, flight delays, emergency evacuation, and 24/7 assistance services. Coverage varies by plan, so it's important to choose one that meets your specific needs."
        },
        {
            question: "When should I purchase travel insurance?",
            answer: "For maximum coverage, especially for trip cancellation benefits, it's best to purchase insurance soon after making your initial trip deposit. Contact us for specific recommendations based on your travel plans."
        },
        {
            question: "Are pre-existing medical conditions covered?",
            answer: "Many plans offer pre-existing condition coverage options. Our insurance specialists can help you find a plan that addresses your specific health concerns and requirements. Contact us for personalized advice."
        }
    ];


    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);
    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="travel-insurance" />

            {/* Main content */}
            <div className="service-hero">
                <img
                    src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                    alt="Travel Insurance"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Travel Insurance</h1>
                    <p className="service-subtitle">
                        Comprehensive travel insurance for worry-free journeys.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png"
                alt="Travel Insurance Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png" alt="Insurance Icon" className="section-icon" />
                        Travel with Confidence and Peace of Mind
                    </h2>
                    <p>
                        At Excellent Travels, we understand that even the most carefully planned trips can face unexpected challenges.
                        Our comprehensive travel insurance options provide protection against a wide range of situations,
                        allowing you to enjoy your journey with complete peace of mind.
                    </p>
                    <p>
                        Our travel insurance services include:
                    </p>
                    <ul>
                        <li>Personalized insurance recommendations based on your specific trip and needs</li>
                        <li>Access to top-rated global insurance providers</li>
                        <li>Competitive rates and exclusive package deals</li>
                        <li>Assistance with policy selection and understanding coverage details</li>
                        <li>Support with claims process if needed during or after your trip</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="/Assets/image/travel-insurance.jpg"
                            alt="Travel Insurance Protection"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Comprehensive protection for your travel investments and peace of mind</div>
                    </div>

                    <p>
                        We believe that travel insurance is an essential part of responsible travel planning,
                        and we're committed to helping you find the right coverage at the right price.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2921/2921222.png" alt="Coverage Options Icon" className="section-icon" />
                        Insurance Coverage Options
                    </h2>
                    <p>
                        We offer various insurance plans to address different travel needs:
                    </p>
                    <ul>
                        <li><strong>Basic Plans:</strong> Essential coverage for budget-conscious travelers</li>
                        <li><strong>Comprehensive Plans:</strong> Extensive protection with higher coverage limits</li>
                        <li><strong>Premium Plans:</strong> Maximum coverage with additional benefits and services</li>
                        <li><strong>Annual Multi-Trip Plans:</strong> Cost-effective coverage for frequent travelers</li>
                        <li><strong>Specialized Coverage:</strong> Plans for adventure activities, cruises, and business travel</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2921/2921048.png" alt="Key Coverage Icon" className="section-icon" />
                        Key Coverage Areas
                    </h2>
                    <p>
                        Our insurance plans typically cover:
                    </p>
                    <ul>
                        <li><strong>Trip Cancellation & Interruption:</strong> Reimbursement for prepaid, non-refundable expenses if you need to cancel or cut short your trip for covered reasons</li>
                        <li><strong>Emergency Medical Expenses:</strong> Coverage for unexpected illness or injury during your trip</li>
                        <li><strong>Emergency Medical Evacuation:</strong> Transportation to the nearest adequate medical facility or back home if necessary</li>
                        <li><strong>Baggage Loss & Delay:</strong> Compensation for lost, stolen, or damaged baggage and essentials when luggage is delayed</li>
                        <li><strong>Travel Delays:</strong> Coverage for additional accommodation and meal expenses during extended delays</li>
                        <li><strong>Missed Connections:</strong> Assistance when travel delays cause you to miss a cruise or tour departure</li>
                        <li><strong>24/7 Emergency Assistance:</strong> Round-the-clock support wherever you are in the world</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2921/2921124.png" alt="Why It Matters Icon" className="section-icon" />
                        Why Travel Insurance Matters
                    </h2>
                    <p>
                        Travel insurance is particularly important for:
                    </p>
                    <ul>
                        <li>International travel where your domestic health insurance may not provide coverage</li>
                        <li>Expensive trips with significant pre-paid, non-refundable costs</li>
                        <li>Travel to remote or developing regions with limited medical facilities</li>
                        <li>Trips during hurricane season or to regions with unpredictable weather</li>
                        <li>Adventure activities and sports that carry higher risk of injury</li>
                        <li>Travelers with pre-existing medical conditions</li>
                        <li>Senior travelers who may face higher risks of medical issues</li>
                    </ul>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png"
                        alt="Travel Insurance Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Choose Our Insurance Services</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921048.png" alt="Recommendations Icon" />
                            </span>
                            Unbiased recommendations
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921222.png" alt="Compare Icon" />
                            </span>
                            Compare multiple providers
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921124.png" alt="Guidance Icon" />
                            </span>
                            Expert guidance on coverage options
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="Claims Icon" />
                            </span>
                            Assistance with claims process
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Rates Icon" />
                            </span>
                            Special rates for group policies
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png" alt="Coverage Icon" />
                            </span>
                            Coverage for all age groups
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933087.png" alt="Destination Icon" />
                            </span>
                            Policies for any destination
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Protect Your Travel Investment</h2>
                <p>
                    Contact our insurance specialists for personalized coverage options and pricing tailored to your specific travel needs.
                </p>
                <Link to="/contact" className="cta-button">
                    Request Insurance Information
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Insured Travelers Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default TravelInsurance;